import React, { useState, useEffect } from "react";
import { apiFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Hebrew labels for the normalized event taxonomy the collector writes.
const EVENT_LABEL = {
  insider_buy: "רכישה",
  insider_sell: "מכירה",
  insider_award: "הענקה",
  insider_exercise: "מימוש",
  insider_tax: "ניכוי מס",
  insider_gift: "מתנה",
  insider_disposition: "העברה",
  insider_conversion: "המרה",
  insider_other: "מסחר פנים",
  rating_upgrade: "העלאת דירוג",
  rating_downgrade: "הורדת דירוג",
  rating_initiated: "תחילת סיקור",
  rating_maintained: "אישרור דירוג",
  rating_change: "עדכון דירוג",
  earnings: "דוחות",
  filing: "דיווח",
  company_news: "חדשות חברה",
  market_news: "שוק",
  other: "אחר",
};

// The two that actually signal a decision get colour; the rest stay neutral.
const EVENT_TONE = {
  insider_buy: "good",
  rating_upgrade: "good",
  insider_sell: "bad",
  rating_downgrade: "bad",
};

function formatDate(value) {
  const d = new Date(value);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 60) return `לפני ${Math.max(1, diffMin)} דקות`;
  if (diffMin < 60 * 24) return `לפני ${Math.floor(diffMin / 60)} שעות`;
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "short", year: "numeric" });
}

function StockNewsModal({ symbol, onClose }) {
  const { token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiFetch(`/api/news?symbol=${encodeURIComponent(symbol)}&limit=50`, { token })
      .then((data) => {
        if (!cancelled) setNews(data.news || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [symbol, token]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          &times;
        </button>
        <div className="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9h4" />
            <line x1="18" y1="14" x2="10" y2="14" />
            <line x1="18" y1="18" x2="10" y2="18" />
            <line x1="14" y1="6" x2="10" y2="6" />
          </svg>
        </div>
        <h2>
          אירועים ב-<span dir="ltr">{symbol}</span>
        </h2>

        {loading ? (
          <div className="loader-container">
            <div className="loader" />
          </div>
        ) : error ? (
          <div className="modal-status error">{error}</div>
        ) : news.length === 0 ? (
          <p className="modal-desc">
            עדיין לא נאספו אירועים למניה הזו. האיסוף רץ ברקע ומתעדכן מעצמו.
          </p>
        ) : (
          <div className="news-list">
            {news.map((n) => (
              <a
                key={n.id}
                className="news-row"
                href={n.url || undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="news-row-top">
                  <span className={`news-tag${EVENT_TONE[n.event_type] ? ` ${EVENT_TONE[n.event_type]}` : ""}`}>
                    {EVENT_LABEL[n.event_type] || n.event_type}
                  </span>
                  <time className="news-date">{formatDate(n.published_at)}</time>
                </div>
                <div className="news-title" dir="auto">
                  {n.title}
                </div>
                {n.source_name && <div className="news-source">{n.source_name}</div>}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default StockNewsModal;
